import { useState } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';

import { APPROXIMATE_TOTAL, IGlobalPricingLineItem } from 'checkout/hooks/useCheckoutGlobalPricing';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

const InstantShipModal = dynamic(
  () => import('productTemplate/components/modals/InstantShipModal'),
  { ssr: false },
);

export const GlobalPricingLineItem = ({
  internationalCheckoutNote,
  isOffer = false,
  isNegative,
  isRefund,
  linkPriorityModal,
  localizedPrice,
  options,
  productTemplateId,
  title,
}: IGlobalPricingLineItem) => {
  const [isInstantShipModalOpen, setIsInstantShipModalOpen] = useState<boolean>(false);
  const isOfferSettledInUsd = useFeatureFlag(FeatureFlags.TEMP_SETTLE_OFFERS_IN_USD);

  if (!linkPriorityModal) {
    return (
      <>
        <GlobalPricingLineItem.Title
          data-qa="checkout_price_breakdown_item_title"
          $padded={options?.padded}
          $isRefund={isRefund}
        >
          {title} {internationalCheckoutNote && `(${internationalCheckoutNote})`}
        </GlobalPricingLineItem.Title>
        <GlobalPricingLineItem.Price data-qa="checkout_price_breakdown_item_price">
          {isNegative && '-'}
          {formatMoneyObject(localizedPrice, {
            showInUsd: isOffer && isOfferSettledInUsd && title !== APPROXIMATE_TOTAL,
          })}
        </GlobalPricingLineItem.Price>
      </>
    );
  }

  const handleInstantShipClick = () => {
    setIsInstantShipModalOpen(true);
  };

  const handleInstantShipClose = () => {
    setIsInstantShipModalOpen(false);
  };

  return (
    <>
      <GlobalPricingLineItem.ClickableTitle
        onClick={handleInstantShipClick}
        $padded={options?.padded}
        $isRefund={isRefund}
        data-qa="checkout_price_breakdown_item_title"
      >
        {title}
      </GlobalPricingLineItem.ClickableTitle>
      <GlobalPricingLineItem.ClickablePrice
        onClick={handleInstantShipClick}
        data-qa="checkout_price_breakdown_item_price"
      >
        {isNegative && '-'}
        {formatMoneyObject(localizedPrice, {
          showInUsd: isOffer && isOfferSettledInUsd && title !== APPROXIMATE_TOTAL,
        })}
      </GlobalPricingLineItem.ClickablePrice>
      {isInstantShipModalOpen && (
        <InstantShipModal productTemplateId={productTemplateId} onClose={handleInstantShipClose} />
      )}
    </>
  );
};

GlobalPricingLineItem.Title = styled.div<{ $padded: boolean; $isRefund: boolean }>`
  grid-column-start: ${({ $padded }) => ($padded ? 'sub-line-item' : '1')};
  grid-column-end: price-start;
  padding-top: 4px;
  ${({ $isRefund, theme }) => $isRefund && `color: ${theme.colors.red};`}
`;

GlobalPricingLineItem.Price = styled.div`
  text-align: right;
  grid-column-start: price-start;
  grid-column-end: price-end;
  padding-top: 4px;
`;

GlobalPricingLineItem.ClickableTitle = styled(GlobalPricingLineItem.Title)`
  cursor: pointer;
`;

GlobalPricingLineItem.ClickablePrice = styled(GlobalPricingLineItem.Price)`
  cursor: pointer;
`;
